import { Chip, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { SalesHarvestContactRow, SalesHarvestOrg } from "@xop/dashboard-shared";

import { verticalLabel } from "../components/VerticalBadge.js";
import { EmailStatusBadge } from "./EmailStatusBadge.js";

/**
 * The harvester pool's two tables — organisations and contacts — shared by
 * Sales ▸ Contact Directory (the whole pool) and a campaign's results page
 * (`campaigns/CampaignResultsPage.tsx`, one campaign). Rows open the
 * `OrgDialog` / `ContactDialog`; the caller owns those.
 */

// Friendly vertical labels reuse the ONE canonical map (`VerticalBadge`'s
// `verticalLabel`) so the harvester directory speaks the exact same vocabulary
// as the Leads list — granular energy sub-sectors (Oil / Gas / LNG / Nuclear /
// Wind & Renewables / Power & Utilities) and the LEGACY lumped slugs folded onto
// their granular chip (never a combined "Oil & Gas" / "Nuclear / Power" pill).

function IndustryChip({ slug }: { slug: string }): JSX.Element {
  return (
    <Chip
      label={verticalLabel(slug)}
      size="small"
      sx={{
        bgcolor: "surfaceSoft",
        color: "text.secondary",
        fontWeight: 500,
        borderRadius: 1,
      }}
    />
  );
}

/** The lead-consumption state — the lead agent draining the pool into leads.
 * When an org was promoted into a lead, the chip LINKS straight to that lead's
 * full profile (evidence + "Qualify for CRM") on the Leads page, so the
 * operator can jump from a harvested org to its lead in one click. */
function PromotionChip({
  state,
  leadId,
}: {
  state: string;
  leadId?: string | null;
}): JSX.Element {
  const map: Record<string, { label: string; color: "default" | "success" }> = {
    promoted: { label: "Lead created", color: "success" },
    pending: { label: "Not yet evaluated", color: "default" },
    rejected: { label: "No XOP fit", color: "default" },
    exists: { label: "Already a lead", color: "default" },
  };
  const { label, color } = map[state] ?? { label: state, color: "default" as const };

  if (state === "promoted" && leadId) {
    return (
      <RouterLink
        to={`/sales/leads/${leadId}`}
        onClick={(e) => e.stopPropagation()}
        style={{ textDecoration: "none" }}
        aria-label="View this lead"
      >
        <Chip
          label={label}
          size="small"
          color="success"
          variant="filled"
          clickable
          sx={{ borderRadius: 1, fontWeight: 500 }}
        />
      </RouterLink>
    );
  }

  return (
    <Chip
      label={label}
      size="small"
      color={color}
      variant={color === "success" ? "filled" : "outlined"}
      sx={{ borderRadius: 1, fontWeight: 500 }}
    />
  );
}

export function OrgsTable({ items, onOpen }: { items: SalesHarvestOrg[]; onOpen: (id: string) => void }): JSX.Element {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Organisation</TableCell>
          <TableCell>Industry</TableCell>
          <TableCell>Country</TableCell>
          <TableCell>Website</TableCell>
          <TableCell align="right">Contacts</TableCell>
          <TableCell>Lead</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((o) => (
          <TableRow key={o.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpen(o.id)}>
            <TableCell sx={{ fontWeight: 500 }}>{o.name}</TableCell>
            <TableCell>
              <IndustryChip slug={o.vertical} />
            </TableCell>
            <TableCell>{o.country || "—"}</TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              {o.domain ? (
                <Link href={`https://${o.domain}`} target="_blank" rel="noreferrer" underline="hover">
                  {o.domain}
                </Link>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell align="right">{o.contact_count}</TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <PromotionChip state={o.promotion_state} leadId={o.promoted_lead_id} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ContactsTable({
  items, onOpen,
}: {
  items: SalesHarvestContactRow[];
  onOpen: (c: SalesHarvestContactRow) => void;
}): JSX.Element {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Title</TableCell>
          <TableCell>Email</TableCell>
          <TableCell>Phone</TableCell>
          <TableCell>Organisation</TableCell>
          <TableCell>Industry</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((c) => (
          <TableRow key={c.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpen(c)}>
            <TableCell sx={{ fontWeight: 500 }}>{c.full_name}</TableCell>
            <TableCell>{c.title || "—"}</TableCell>
            <TableCell>
              {c.email ? (
                <Stack spacing={0.5}>
                  <span>{c.email}</span>
                  <EmailStatusBadge contact={c} />
                </Stack>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell>{c.phone || "—"}</TableCell>
            <TableCell>{c.org_name}</TableCell>
            <TableCell>
              <IndustryChip slug={c.vertical} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
